<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import DashboardAppModal from './DashboardAppModal.vue';
import DashboardAppsRow from './DashboardAppsRow.vue';

export default {
  components: {
    DashboardAppModal,
    DashboardAppsRow,
  },
  data() {
    return {
      loading: {},
      showDashboardAppPopup: false,
      showDeleteConfirmationPopup: false,
      selectedApp: {},
      mode: 'CREATE',
    };
  },
  computed: {
    ...mapGetters({
      records: 'dashboardApps/getRecords',
      uiFlags: 'dashboardApps/getUIFlags',
    }),
  },
  mounted() {
    this.$store.dispatch('dashboardApps/get');
  },
  methods: {
    toggleDashboardAppPopup() {
      this.showDashboardAppPopup = !this.showDashboardAppPopup;
      this.selectedApp = {};
    },
    openDeletePopup(response) {
      this.showDeleteConfirmationPopup = true;
      this.selectedApp = response;
    },
    openCreatePopup() {
      this.mode = 'CREATE';
      this.selectedApp = {};
      this.showDashboardAppPopup = true;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    editApp(app) {
      this.loading[app.id] = true;
      this.mode = 'UPDATE';
      this.selectedApp = app;
      this.showDashboardAppPopup = true;
    },
    confirmDeletion() {
      this.loading[this.selectedApp.id] = true;
      this.closeDeletePopup();
      this.deleteApp(this.selectedApp.id);
    },
    async deleteApp(id) {
      try {
        await this.$store.dispatch('dashboardApps/delete', id);
        useAlert(
          this.$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DELETE.API_SUCCESS')
        );
      } catch (error) {
        useAlert(
          this.$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DELETE.API_ERROR')
        );
      }
    },
    goBack() {
      this.$router.push({ name: 'settings_applications' });
    },
  },
};
</script>

<template>
  <div class="dashboard-apps-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="page-header flex items-center justify-between px-6 border-b border-slate-200 flex-shrink-0"
      style="height: 50px"
    >
      <div class="flex items-center gap-4">
        <button class="back-btn" @click="goBack">
          <span class="i-ph-arrow-left-bold size-4" />
          <span>{{ $t('INTEGRATION_SETTINGS.HEADER') }}</span>
        </button>
        <span class="text-slate-300">•</span>
        <h1 class="text-lg font-semibold text-slate-800">
          {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.TITLE') }}
        </h1>
      </div>
      <button class="primary-action-btn" @click="openCreatePopup">
        <span class="i-ph-plus-bold size-4" />
        {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.HEADER_BTN_TXT') }}
      </button>
    </header>

    <!-- Description -->
    <div class="px-6 py-3 border-b border-slate-200">
      <p class="text-sm text-n-slate-11">
        {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DESCRIPTION') }}
      </p>
    </div>

    <!-- Stats -->
    <div class="px-6 py-3 border-b border-slate-200">
      <div class="text-sm text-n-slate-11">
        {{ records.length }} apps configured
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto p-6">
      <!-- Loading -->
      <div
        v-if="uiFlags.isFetching"
        class="flex items-center justify-center py-20"
      >
        <div class="loading-spinner" />
      </div>

      <!-- Empty State -->
      <div v-else-if="!records.length" class="empty-state">
        <div class="empty-icon-wrap">
          <span class="i-ph-squares-four-bold size-12" />
        </div>
        <h3 class="text-xl font-semibold text-slate-800 mb-2">
          {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.LIST.404') }}
        </h3>
        <p class="text-n-slate-11 text-sm mb-6">
          Create a dashboard app to embed external content
        </p>
        <button class="primary-action-btn" @click="openCreatePopup">
          <span class="i-ph-plus-bold size-4" />
          {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.HEADER_BTN_TXT') }}
        </button>
      </div>

      <!-- Apps Grid -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div v-for="app in records" :key="app.id" class="app-card group">
          <div class="flex items-start justify-between mb-4">
            <div class="app-icon">
              <span class="i-ph-squares-four-bold size-5" />
            </div>
            <div
              class="app-actions opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2"
            >
              <button class="action-btn" title="Edit" @click="editApp(app)">
                <span class="i-ph-pencil-simple-bold size-4" />
              </button>
              <button
                class="action-btn action-btn-danger"
                title="Delete"
                @click="openDeletePopup(app)"
              >
                <span class="i-ph-trash-bold size-4" />
              </button>
            </div>
          </div>
          <div class="flex-1">
            <h3 class="text-base font-semibold text-slate-800 mb-2">
              {{ app.title }}
            </h3>
            <p class="text-xs text-n-slate-11 truncate">
              {{ app.content?.[0]?.url || 'No URL configured' }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <DashboardAppModal
      v-if="showDashboardAppPopup"
      :show="showDashboardAppPopup"
      :mode="mode"
      :selected-app-data="selectedApp"
      @close="toggleDashboardAppPopup"
    />

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DELETE.TITLE')"
      :message="
        $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DELETE.MESSAGE', {
          appName: selectedApp.title,
        })
      "
      :confirm-text="
        $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DELETE.CONFIRM_YES')
      "
      :reject-text="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DELETE.CONFIRM_NO')"
    />
  </div>
</template>

<style scoped>
.dashboard-apps-page {
  background-color: #ffffff;
}

.page-header {
  background-color: #ffffff !important;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.back-btn:hover {
  color: #111827;
  background: #f9fafb;
}

.primary-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  background-color: #1f2937;
  color: #fff;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.primary-action-btn:hover {
  background-color: #374151;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: #1f2937;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.empty-icon-wrap {
  width: 100px;
  height: 100px;
  border-radius: 24px;
  background: linear-gradient(135deg, #f3f4f6 0%, #f9fafb 100%);
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  margin-bottom: 24px;
}

.app-card {
  display: flex;
  flex-direction: column;
  padding: 20px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  transition: all 0.2s ease;
  min-height: 140px;
}

.app-card:hover {
  border-color: #d1d5db;
  background: #f9fafb;
}

.app-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f3f4f6 0%, #f9fafb 100%);
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4b5563;
}

.action-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  border: none;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;
}

.action-btn:hover {
  background: #f3f4f6;
  color: #111827;
}

.action-btn-danger:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.bg-n-background),
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2) {
  background: #ffffff !important;
}
:deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}
:deep(label) {
  color: #111827 !important;
}
:deep(input:not(.reset-base)),
:deep(select:not(.reset-base)),
:deep(textarea:not(.reset-base)) {
  background: #ffffff !important;
  border-color: #d1d5db !important;
  color: #111827 !important;
}
</style>
